import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { MenuItem } from "@mui/material";
import * as React from "react";

import { Menu } from "@/shared/components/Menu";
import {
  formatTimeByMode,
  TIME_DISPLAY_MODES,
  TimeDisplayMode,
} from "@/shared/components/visualization/WorkspaceCtx";
import { usePopupState } from "@/shared/hooks";

import styles from "./TimeDisplayField.module.css";

// Short labels for the trigger and dropdown. The seconds variant is suffixed
// "(s)" to distinguish it from the HH:MM:SS variant, which keeps the unadorned
// "Elapsed" label. Decoupled from the enum string values so persisted
// preferences (the values behind useTimeDisplayMode) remain stable.
const MODE_LABELS: Record<TimeDisplayMode, string> = {
  [TimeDisplayMode.Elapsed]: "Elapsed (s)",
  [TimeDisplayMode.ElapsedTime]: "Elapsed",
  [TimeDisplayMode.Log]: "Log",
  [TimeDisplayMode.Local]: "Local",
  [TimeDisplayMode.UTC]: "UTC",
};

interface TimeModePickerProps {
  mode: TimeDisplayMode;
  currentTimeNs: bigint;
  earliestTimeNs: bigint | null;
  onModeChange: (mode: TimeDisplayMode) => void;
}

export function TimeModePicker({
  mode,
  currentTimeNs,
  earliestTimeNs,
  onModeChange,
}: TimeModePickerProps) {
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const popup = usePopupState();

  const handleSelect = (newMode: TimeDisplayMode) => {
    onModeChange(newMode);
    popup.close();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.modeButton}
        aria-label="Time display mode"
        aria-haspopup="menu"
        aria-expanded={popup.isOpen}
        onClick={() => {
          if (buttonRef.current) {
            popup.open({ anchorEl: buttonRef.current });
          }
        }}
      >
        <span>{MODE_LABELS[mode]}</span>
        <ArrowDropDownIcon fontSize="small" />
      </button>
      <Menu
        anchorEl={popup.anchor}
        open={popup.isOpen}
        onClose={popup.close}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        // StyledMenu's `& .MuiPaper-root` rule sets marginTop: 8px for the
        // default downward opening; we're flipping upward, so override with
        // higher specificity to put the gap above the trigger instead.
        slotProps={{
          paper: {
            sx: {
              "&&.MuiPaper-root": { marginTop: 0, marginBottom: 1 },
            },
          },
        }}
      >
        {TIME_DISPLAY_MODES.map((m) => (
          <MenuItem
            key={m}
            selected={m === mode}
            onClick={() => handleSelect(m)}
            className={styles.menuItem}
          >
            <span className={styles.menuItemMode}>{MODE_LABELS[m]}</span>
            <span className={styles.menuItemTime}>
              {formatTimeByMode(currentTimeNs, m, earliestTimeNs)}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
